'use client';

import Modal from './Modal';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import useLoginModal from '@/app/hooks/useLoginModal';
import CustomButton from '../forms/CustomButton';
import { handleLogin } from '@/app/lib/actions';
import apiService from '@/app/services/apiService';
import GoogleLoginButton from '../GoogleLoginButton';
// import { GoogleOAuthProvider } from '@react-oauth/google';
import { useAuthStore } from '@/app/hooks/auth';

const Login = () => {
  const router = useRouter();
  const login = useLoginModal();
  const { setAuth, setUser } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const [showResetForm, setShowResetForm] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetMessage, setResetMessage] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID as string;

  const submitLogin = async () => {
    const formData = {
      email,
      password,
    };

    try {
      const response = await apiService.postWithoutToken('/api/auth/login/', JSON.stringify(formData));

      if (response.access) {
        // 1️⃣ Salva tokens no Zustand
        setAuth({ accessToken: response.access, refreshToken: response.refresh });

        // 2️⃣ Salva userId no cookie httpOnly via server
        await handleLogin(response.user.pk, response.access, response.refresh);

        // 3️⃣ Busca dados completos do usuário
        try {
          const userData = await apiService.getWithToken('/api/user/me/');
          setUser(userData);
        } catch (error) {
          console.error('Erro ao buscar dados do usuário:', error);
        }

        login.close();
        // router.push('/landing');
        router.refresh();
      } else {
        const tmpErrors: string[] = Object.values(response).map((error: any) => error);
        setErrors(tmpErrors);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setErrors(['Email ou senha inválida.']);
    }
  };

  const handleSendResetEmail = async () => {
    setResetLoading(true)
    setResetMessage('')
    try {
      await apiService.postWithoutTokenPlus('/api/auth/password/reset/', { email: resetEmail })
      setResetMessage('📩 Um link de redefinição foi enviado para seu email.')
    } catch (error) {
      setResetMessage('❌ Erro ao enviar email de redefinição.')
      console.error(error)
    } finally {
      setResetLoading(false)
    }
  }

  const content = (
    <>
      <form action={submitLogin} className="space-y-4">
        <input
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          type="email"
          className="w-full h-[54px] px-4 border border-gray-300 rounded-xl"
        />

        <input
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          className="w-full h-[54px] px-4 border border-gray-300 rounded-xl"
        />

        <div className="text-right">
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => setShowResetForm(true)}
          >
            Esqueceu sua senha?
          </button>
        </div>

        {errors.map((error, index) => (
          <div
            key={`error_${index}`}
            className="p-5 bg-purple-700 text-white font-semibold rounded-xl opacity-80 transition-opacity duration-300"
          >
            {error}
          </div>
        ))}

        <CustomButton
          label="Entrar"
          className="w-full py-4 bg-nat-reggaex hover:bg-nat-reggaex-dark text-white text-center rounded-xl transition cursor-pointer"
          onClick={submitLogin}
        />
      </form>

      {showResetForm && (
        <div className="mt-6 space-y-3">
          <p className="text-sm text-gray-700">Digite seu email para receber o link de redefinição:</p>
          <input
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            placeholder="Seu email"
            className="w-full h-[50px] px-4 border border-gray-300 rounded-xl"
          />
          <button
            onClick={handleSendResetEmail}
            disabled={resetLoading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
          >
            {resetLoading ? 'Enviando...' : 'Enviar link por email'}
          </button>
          {resetMessage && <p className="text-sm text-green-700">{resetMessage}</p>}
        </div>
      )}

      <div className="mt-6 px-2 sm:px-0">
        <div className="flex items-center justify-center">
          <div className="w-full border-t border-gray-300" />
          <span className="px-3 text-sm text-gray-500 whitespace-nowrap">ou continue com</span>
          <div className="w-full border-t border-gray-300" />
        </div>

        <div className="mt-6 flex justify-center">
          <div className="w-full flex justify-center">
            <div className="min-w-[260px] max-w-[320px] w-full">
              <div
                className="
                  relative inline-flex w-full min-h-[44px] mb-3
                  items-center justify-center overflow-hidden
                  rounded-xl bg-green-100
                  isolation-isolate
                "
                style={{ transform: 'none' }}
              >
                <GoogleLoginButton />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={login.isOpen}
      close={login.close}
      label="Entrar"
      content={content}
    />
  );
};

export default Login;
